import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import * as repo from '../data/usersRepository';

export const usersKeys = {
  overview: ['users', 'overview'] as const,
  ministers: (q: string) => ['users', 'ministers', q] as const,
  suggestion: (ministerId: string) => ['users', 'suggestion', ministerId] as const,
};

export function useUsersOverview() {
  return useQuery({ queryKey: usersKeys.overview, queryFn: repo.getUsersOverview });
}

export function useMinisterSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: usersKeys.ministers(q),
    queryFn: () => repo.searchMinisters(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useMinisterSuggestion(ministerId: string | null) {
  return useQuery({
    queryKey: usersKeys.suggestion(ministerId ?? ''),
    queryFn: () => repo.suggestMinisterUsername(ministerId as string),
    enabled: !!ministerId,
  });
}

/** Mutation that toasts the (Spanish) error and refreshes the users list and own permissions */
export function useUsersMutation<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>,
  options?: { success?: string },
) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (options?.success) toast({ title: options.success });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      void qc.invalidateQueries({ queryKey: ['current_user_permissions'] });
    },
    onError: (err) => toast({ title: err instanceof Error ? err.message : 'Error inesperado', variant: 'destructive' }),
  });
}
